import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Component to handle requisition approval link authentication and redirect to requisition
 */
const RequisitionApprovalLinkHandler = () => {
  const { requisitionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithApprovalToken, isAuthenticated } = useAuth();
  const [error, setError] = useState(null);
  const [authenticating, setAuthenticating] = useState(true);

  useEffect(() => {
    const handleApprovalLink = async () => {
      // Get approval token from URL parameters
      const urlParams = new URLSearchParams(location.search);
      const approvalToken = urlParams.get('approval_token');

      if (!approvalToken || !requisitionId) {
        setError('Invalid approval link - missing token or requisition ID');
        setAuthenticating(false);
        return;
      }

      try {
        setAuthenticating(true);
        console.log('🔐 Processing requisition approval link for requisition:', requisitionId);

        // Authenticate using the approval token via AuthContext
        const result = await loginWithApprovalToken(approvalToken, requisitionId);

        if (result && result.status === 1) {
          console.log('✅ Requisition approval token authentication successful, redirecting...');
          
          // Redirect to the requisition details page after successful authentication
          navigate(`/dashboard/requisitions/${requisitionId}`, { replace: true });
        } else {
          setError('Authentication failed. The approval link may be invalid or expired.');
          setAuthenticating(false);
        }
      } catch (err) {
        console.error('❌ Requisition approval link authentication error:', err);
        setError(err.message || 'Failed to authenticate. The approval link may be invalid or expired.');
        setAuthenticating(false);
      }
    };

    // Only proceed if not already authenticated
    if (!isAuthenticated) {
      handleApprovalLink();
    } else {
      // Already authenticated, just redirect to requisition
      console.log('✅ User already authenticated, redirecting to requisition...');
      navigate(`/dashboard/requisitions/${requisitionId}`, { replace: true });
    }
  }, [requisitionId, location.search, loginWithApprovalToken, isAuthenticated, navigate]);

  if (authenticating) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Authenticating and loading requisition...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/login')} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default RequisitionApprovalLinkHandler;